import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type AutomationTriggerEvent = "PROCEDURE_STATUS_CHANGED" | "TAX_OBLIGATION_PAID" | "INVOICE_PAID" | "TASK_COMPLETED";
export type AutomationActionType = "CREATE_FOLLOWUP_TASK" | "CREATE_NEXT_TAX_OBLIGATION";
export type AutomationPeriodType = "MONTHLY" | "QUARTERLY" | "ANNUAL";
export type AutomationRunStatus = "SUCCESS" | "FAILED";

export const TRIGGER_EVENT_LABEL: Record<AutomationTriggerEvent, string> = {
  TASK_COMPLETED: "Tarea completada",
  PROCEDURE_STATUS_CHANGED: "Trámite cambia de estado",
  TAX_OBLIGATION_PAID: "Obligación tributaria pagada",
  INVOICE_PAID: "Factura pagada",
};

export const ACTION_TYPE_LABEL: Record<AutomationActionType, string> = {
  CREATE_FOLLOWUP_TASK: "Crear tarea de seguimiento",
  CREATE_NEXT_TAX_OBLIGATION: "Crear la siguiente obligación tributaria",
};

export const PERIOD_TYPE_LABEL: Record<AutomationPeriodType, string> = {
  MONTHLY: "Mensual",
  QUARTERLY: "Trimestral",
  ANNUAL: "Anual",
};

// Trámites usa su propio catálogo de estados (Fase 11); se repite aquí para
// no crear una dependencia cruzada solo por un select.
export const PROCEDURE_STATUS_OPTIONS = ["PENDIENTE", "EN_PROCESO", "EN_REVISION", "APROBADO", "FINALIZADO", "CANCELADO"];

export interface AutomationRuleRecord {
  id: string;
  name: string;
  description: string | null;
  triggerEvent: AutomationTriggerEvent;
  triggerStatus: string | null;
  actionType: AutomationActionType;
  actionConfig: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  createdBy?: { id: string; fullName: string; email: string } | null;
}

export interface AutomationRunRecord {
  id: string;
  ruleId: string;
  sourceEntityType: string;
  sourceEntityId: string;
  status: AutomationRunStatus;
  resultEntityType: string | null;
  resultEntityId: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export function useAutomationRules() {
  return useQuery({
    queryKey: ["automations"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<AutomationRuleRecord[]>>("/automations");
      return data.data;
    },
  });
}

export function useAutomationRuns(ruleId: string | null) {
  return useQuery({
    queryKey: ["automations", ruleId, "runs"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<AutomationRunRecord[]>>(`/automations/${ruleId}/runs`);
      return data.data;
    },
    enabled: !!ruleId,
  });
}

export interface AutomationRulePayload {
  name: string;
  description?: string;
  triggerEvent: AutomationTriggerEvent;
  triggerStatus?: string;
  actionType: AutomationActionType;
  actionConfig: Record<string, unknown>;
  isActive?: boolean;
}

export function useCreateAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AutomationRulePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<AutomationRuleRecord>>("/automations", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["automations"] }),
  });
}

export function useUpdateAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<AutomationRulePayload> & { id: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<AutomationRuleRecord>>(`/automations/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["automations"] }),
  });
}

export function useDeleteAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/automations/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["automations"] }),
  });
}
